/**
 * Builds a `mailto:` URL for the Draft email action on a contact.
 *
 * WHY encodeURIComponent, NOT URLSearchParams
 * --------------------------------------------
 * `URLSearchParams` encodes a space as `+`, which is right for a form body
 * and wrong here: most mail clients decode a `+` in a mailto link literally,
 * so a rendered subject like "Hi & bye" would arrive with a stray plus sign
 * where the space should be. `encodeURIComponent` encodes a space as `%20`,
 * which every mail client understands, so the query string is built by hand
 * with it rather than through the URL-building helper that looks obvious.
 *
 * WHY NULL, NOT AN EMPTY STRING
 * ------------------------------
 * A contact with no email address is a normal state, not an error -- see
 * TouchButtons, which disables the Draft email button and says why rather
 * than calling this and getting back something that silently does nothing.
 */
export function buildMailto(
  to: string | null | undefined,
  subject: string,
  body: string,
): string | null {
  if (!to) return null;
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * The address a draft goes to: the FIRST entry of `email_jsonb`.
 *
 * One rule in one place, because two entry points now ask the question --
 * TouchButtons on the contact page and TaskDraftEmail on a task's own menu --
 * and they must not disagree. `email_jsonb` is an ordered array of
 * `{email, type}` whose first entry is what every other screen in this app
 * shows as the contact's address; picking a different one here would send mail
 * somewhere the page never displayed.
 *
 * An empty array is NORMAL, not an error. See the note above.
 */
export function primaryEmail(
  contact: { email_jsonb?: { email: string }[] } | null | undefined,
): string | null {
  return contact?.email_jsonb?.[0]?.email ?? null;
}
